/**
 * Incidents + IFTTT delivery history (plan §18 "Incidents"). Incidents keep
 * their ORIGINAL first/last-seen timestamps and occurrence counts (offline
 * devices report late); deliveries show every attempt with redacted errors.
 */
import { useState } from "react";
import { Badge, Button, Group, Paper, Select, Stack, Tabs, Table, Text, TextInput, Title } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { upgradeApi } from "../../api/fleet";
import { useAuth } from "../../hooks/useAuth";

const SEV_COLOR: Record<string, string> = { info: "gray", warning: "yellow", error: "orange", critical: "red" };
const DLV_COLOR: Record<string, string> = { queued: "blue", sending: "blue", sent: "green", failed: "orange", exhausted: "red", suppressed: "gray" };

export function IncidentsPage() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>("open");
  const incidents = useQuery({
    queryKey: ["incidents", search, severity, status],
    queryFn: () => upgradeApi.incidents.list({ search: search || undefined, severity: severity ?? undefined, status: status ?? undefined, limit: "200" }),
    refetchInterval: 15_000,
  });
  const deliveries = useQuery({ queryKey: ["deliveries"], queryFn: () => upgradeApi.incidents.deliveries({ limit: "200" }), enabled: isAdmin, refetchInterval: 15_000 });
  const ack = useMutation({ mutationFn: (id: string) => upgradeApi.incidents.acknowledge(id), onSuccess: () => void qc.invalidateQueries({ queryKey: ["incidents"] }) });
  const resolve = useMutation({ mutationFn: (id: string) => upgradeApi.incidents.resolve(id), onSuccess: () => void qc.invalidateQueries({ queryKey: ["incidents"] }) });
  const retry = useMutation({ mutationFn: (id: string) => upgradeApi.incidents.retryDelivery(id), onSuccess: () => void qc.invalidateQueries({ queryKey: ["deliveries"] }) });

  return (
    <>
      <Title order={2} mb="lg">Incidents</Title>
      <Tabs defaultValue="incidents">
        <Tabs.List>
          <Tabs.Tab value="incidents">Warnings & errors</Tabs.Tab>
          {isAdmin && <Tabs.Tab value="deliveries">IFTTT / channel deliveries</Tabs.Tab>}
        </Tabs.List>
        <Tabs.Panel value="incidents" pt="md">
          <Group mb="md">
            <TextInput placeholder="Search message / code / source…" value={search} onChange={(e) => setSearch(e.currentTarget.value)} w={{ base: "100%", sm: 320 }} />
            <Select placeholder="Severity" data={["info", "warning", "error", "critical"]} value={severity} onChange={setSeverity} clearable w={150} />
            <Select placeholder="Status" data={["open", "acknowledged", "resolved"]} value={status} onChange={setStatus} clearable w={160} />
          </Group>
          <Stack gap="xs">
            {(incidents.data ?? []).map((i) => (
              <Paper key={i.id} p="xs" withBorder>
                <Group justify="space-between" wrap="nowrap">
                  <Stack gap={2} style={{ flex: 1 }}>
                    <Group gap="xs">
                      <Badge color={SEV_COLOR[i.severity]}>{i.severity}</Badge>
                      <Text fw={600} size="sm">{i.code}</Text>
                      <Badge variant="outline" size="xs">{i.status}</Badge>
                      {i.occurrences > 1 && <Badge variant="light" size="xs">×{i.occurrences}</Badge>}
                      {typeof i.correlation?.agent_id === "string" && <Badge variant="dot" size="xs">{String(i.correlation.agent_id)}</Badge>}
                    </Group>
                    <Text size="sm">{i.message}</Text>
                    <Text size="xs" c="dimmed">
                      first {new Date(i.first_seen_at).toLocaleString()} · last {new Date(i.last_seen_at).toLocaleString()} · received {new Date(i.received_at).toLocaleString()}
                      {i.source ? ` · ${i.source}` : ""}
                    </Text>
                  </Stack>
                  {isAdmin && i.status !== "resolved" && (
                    <Group gap={4} wrap="nowrap">
                      {i.status === "open" && <Button size="compact-xs" variant="light" onClick={() => ack.mutate(i.id)}>Acknowledge</Button>}
                      <Button size="compact-xs" variant="light" color="green" onClick={() => resolve.mutate(i.id)}>Resolve</Button>
                    </Group>
                  )}
                </Group>
              </Paper>
            ))}
            {!incidents.isLoading && !incidents.data?.length && <Text c="dimmed">No incidents match.</Text>}
          </Stack>
        </Tabs.Panel>
        {isAdmin && (
          <Tabs.Panel value="deliveries" pt="md">
            <Text size="sm" c="dimmed" mb="sm">An HTTP success means IFTTT accepted the request — not that a person saw it. Retries may duplicate; payloads carry stable incident ids.</Text>
            <Paper withBorder p="xs">
              <Table striped>
                <Table.Thead><Table.Tr><Table.Th>Created</Table.Th><Table.Th>Channel</Table.Th><Table.Th>Event</Table.Th><Table.Th>Bot</Table.Th><Table.Th>Status</Table.Th><Table.Th>Attempts</Table.Th><Table.Th>Last error</Table.Th><Table.Th /></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {(deliveries.data ?? []).map((d) => (
                    <Table.Tr key={d.id}>
                      <Table.Td>{new Date(d.created_at).toLocaleString()}</Table.Td>
                      <Table.Td>{d.channel_label ?? "—"} <Text span size="xs" c="dimmed">({d.channel_type})</Text></Table.Td>
                      <Table.Td>{d.event_kind}</Table.Td>
                      <Table.Td>{d.agent_id ?? "—"}</Table.Td>
                      <Table.Td><Badge color={DLV_COLOR[d.status] ?? "gray"}>{d.status}</Badge></Table.Td>
                      <Table.Td>{d.attempts}/{d.max_attempts}{d.attempts_log?.length ? ` (${d.attempts_log.filter((a) => a.ok).length} ok)` : ""}</Table.Td>
                      <Table.Td><Text size="xs" c="red">{d.last_error ?? ""}</Text></Table.Td>
                      <Table.Td>{(d.status === "failed" || d.status === "exhausted") && <Button size="compact-xs" variant="light" onClick={() => retry.mutate(d.id)}>Retry</Button>}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          </Tabs.Panel>
        )}
      </Tabs>
    </>
  );
}
